import React, { useState, useCallback, useMemo } from 'react';
import { Column } from '../../../../utils/tableService';
import { useTranslation } from 'react-i18next';

interface ExtractColumnsModalProps {
  columns: Column[];
  selected: string[];
  onSave: (selectedColumns: string[]) => void;
  onClose: () => void;
  readOnly?: boolean;
}

const ExtractColumnsModal: React.FC<ExtractColumnsModalProps> = React.memo(({ columns, selected, onSave, onClose, readOnly }) => {
  const { t } = useTranslation();
  console.log('[DEBUG] ExtractColumnsModal 렌더링됨', { 
    columnsLength: columns?.length || 0, 
    selectedLength: selected?.length || 0,
    columns,
    selected
  });

  const [checked, setChecked] = useState<string[]>(selected || []);

  const handleToggle = useCallback((colName: string) => {
    setChecked(prev =>
      prev.includes(colName)
        ? prev.filter(n => n !== colName)
        : [...prev, colName]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setChecked(columns.map(col => col.name));
  }, [columns]);

  const handleDeselectAll = useCallback(() => {
    setChecked([]);
  }, []);

  const handleSave = useCallback(() => {
    onSave(checked);
  }, [checked, onSave]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white rounded shadow-lg p-6 min-w-[320px] max-w-[90vw]">
        <h3 className="text-lg font-semibold mb-4">{t('property:ec.title')}</h3>
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm">
            <span className="font-medium">{checked.length}/{columns.length}</span> {t('property:ec.selected')}
          </div>
          {!readOnly && (
            <div className="flex gap-2">
              <button 
                className="px-2 py-1 rounded bg-gray-100 text-xs hover:bg-gray-200" 
                onClick={handleSelectAll}
              >
                {t('property:ec.selectAll')}
              </button>
              <button 
                className="px-2 py-1 rounded bg-gray-100 text-xs hover:bg-gray-200" 
                onClick={handleDeselectAll}
              >
                {t('property:ec.deselectAll')}
              </button>
            </div>
          )}
        </div>
        <div className="max-h-64 overflow-y-auto mb-4 border rounded-md overflow-x-auto">
          {columns.length === 0 ? (
            <div className="text-gray-400">{t('property:ec.noColumns')}</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('property:ec.select')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('property:ec.columnName')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('workflow:field.comment')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('workflow:field.dataType')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {columns.map((col, idx) => (
                  <tr key={col.name} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={checked.includes(col.name)}
                        onChange={() => handleToggle(col.name)}
                        className="mr-2"
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-900 whitespace-nowrap font-mono">{col.name}</td>
                    <td className="px-4 py-2 text-xs text-gray-600 whitespace-nowrap">{col.comment || '-'}</td>
                    <td className="px-4 py-2 text-xs text-gray-600 font-mono whitespace-nowrap">{col.dataType}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex justify-end gap-2">
          {!readOnly && <button className="px-3 py-1 rounded bg-blue-600 text-white" onClick={handleSave}>{t('property:ec.save')}</button>}
          <button className="px-3 py-1 rounded bg-gray-200" onClick={onClose}>{t('property:ec.close')}</button>
        </div>
      </div>
    </div>
  );
});

export default ExtractColumnsModal; 